import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquare, X } from "lucide-react";
import { getLauraAudit } from "@/app/lib/auditflowApi";
import type {
    LauraAudit,
    LauraP0State,
    LauraStageId,
} from "@/app/components/shared/types";
import type { LauraStageState } from "@/app/lib/auditflowApi";
import { LauraStageNavigator } from "./LauraStageNavigator";
import { LauraDeliverablePanel } from "./LauraDeliverablePanel";
import { LauraApproveBar } from "./LauraApproveBar";
import { LauraChatPanel } from "./LauraChatPanel";
import { LauraP0Workspace } from "./p0/LauraP0Workspace";
import { LauraP1Workspace } from "./p1/LauraP1Workspace";
import { LauraP2Workspace } from "./p2/LauraP2Workspace";
import { LauraP3Workspace } from "./p3/LauraP3Workspace";
import { LauraP4Workspace } from "./p4/LauraP4Workspace";
import { LauraP5Workspace } from "./p5/LauraP5Workspace";
import { LauraP6Workspace } from "./p6/LauraP6Workspace";
import { LauraP7Workspace } from "./p7/LauraP7Workspace";
import { RenameableTitle } from "../shared/RenameableTitle";
import { updateLauraAudit } from "@/app/lib/auditflowApi";
import { useSidebar } from "@/app/contexts/SidebarContext";

interface Props {
    auditId: string;
    projectId?: string;
}

type PlanningWorkspaceProps = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: LauraStageState | null) => void;
};

const PLANNING_WORKSPACES: Partial<
    Record<LauraStageId, React.ComponentType<PlanningWorkspaceProps>>
> = {
    p1: LauraP1Workspace,
    p2: LauraP2Workspace,
    p3: LauraP3Workspace,
    p4: LauraP4Workspace,
    p5: LauraP5Workspace,
    p6: LauraP6Workspace,
    p7: LauraP7Workspace,
};

export function LauraAuditView({ auditId }: Props) {
    const [audit, setAudit] = useState<LauraAudit | null>(null);
    const [viewStage, setViewStage] = useState<LauraStageId>("p0");
    const [chatOpen, setChatOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [p0State, setP0State] = useState<LauraP0State | null>(null);
    const [planningStageState, setPlanningStageState] =
        useState<LauraStageState | null>(null);
    const { setSidebarOpen } = useSidebar();

    async function reload() {
        const data = await getLauraAudit(auditId);
        setAudit(data);
        setViewStage(data.current_stage);
        return data;
    }

    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, [auditId]);

    useEffect(() => {
        setSidebarOpen(false);
    }, [setSidebarOpen]);

    useEffect(() => {
        if (!chatOpen) return;
        function onKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") setChatOpen(false);
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [chatOpen]);

    useEffect(() => {
        setPlanningStageState(null);
    }, [viewStage]);

    const handleDeliverableSaved = useCallback(
        (stage: LauraStageId, content: string) => {
            setAudit((a) => {
                if (!a) return a;
                const deliverables = [...(a.deliverables ?? [])];
                const idx = deliverables.findIndex((d) => d.stage === stage);
                if (idx >= 0) {
                    deliverables[idx] = {
                        ...deliverables[idx],
                        content_md: content,
                    };
                } else {
                    deliverables.push({
                        id: "",
                        audit_id: a.id,
                        stage,
                        content_md: content,
                        version: 1,
                        created_at: "",
                        updated_at: "",
                    });
                }
                return { ...a, deliverables };
            });
        },
        [],
    );

    if (loading || !audit) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    const deliverable =
        audit.deliverables?.find((d) => d.stage === viewStage)?.content_md ?? "";

    const PlanningWorkspace = PLANNING_WORKSPACES[viewStage];

    return (
        <div className="flex h-full flex-col">
            <header className="flex items-center justify-between border-b px-4 py-3">
                <RenameableTitle
                    value={audit.title}
                    onCommit={async (title) => {
                        const updated = await updateLauraAudit(audit.id, { title });
                        setAudit((a) => (a ? { ...a, title: updated.title } : a));
                    }}
                />
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{audit.laura_sai_profiles?.name}</span>
                    <button
                        type="button"
                        onClick={() => setChatOpen((o) => !o)}
                        className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm ${
                            chatOpen ? "bg-gray-100" : "hover:bg-gray-50"
                        }`}
                    >
                        <MessageSquare className="h-4 w-4" />
                        Chat
                    </button>
                </div>
            </header>

            <LauraStageNavigator
                currentStage={audit.current_stage}
                completions={audit.stage_completions ?? []}
                outputLanguage={audit.output_language}
                onSelectStage={(s) => setViewStage(s)}
            />

            <div className="relative flex flex-1 min-h-0">
                <div className="flex flex-1 flex-col min-w-0">
                    <div className="flex-1 min-h-0">
                        {viewStage === "p0" ? (
                            <LauraP0Workspace
                                audit={audit}
                                deliverableContent={deliverable}
                                onDeliverableSaved={(content) =>
                                    handleDeliverableSaved("p0", content)
                                }
                                onP0StateChange={setP0State}
                            />
                        ) : PlanningWorkspace ? (
                            <PlanningWorkspace
                                audit={audit}
                                deliverableContent={deliverable}
                                onDeliverableSaved={(content) =>
                                    handleDeliverableSaved(viewStage, content)
                                }
                                onStageStateChange={setPlanningStageState}
                            />
                        ) : (
                            <LauraDeliverablePanel
                                auditId={audit.id}
                                stage={viewStage}
                                content={deliverable}
                                outputLanguage={audit.output_language}
                                onSaved={(content) =>
                                    handleDeliverableSaved(viewStage, content)
                                }
                            />
                        )}
                    </div>
                    <LauraApproveBar
                        audit={audit}
                        viewStage={viewStage}
                        deliverableContent={deliverable}
                        p0State={viewStage === "p0" ? p0State : undefined}
                        planningStageState={
                            PlanningWorkspace ? planningStageState : undefined
                        }
                        onApproved={(updated) => {
                            setAudit((a) =>
                                a
                                    ? {
                                          ...a,
                                          ...updated,
                                          deliverables: a.deliverables,
                                      }
                                    : a,
                            );
                            if (updated.current_stage)
                                setViewStage(updated.current_stage);
                            reload();
                        }}
                    />
                </div>

                {chatOpen && (
                    <>
                        <button
                            type="button"
                            aria-label="Close chat"
                            className="fixed inset-0 z-40 bg-black/15"
                            onClick={() => setChatOpen(false)}
                        />
                        <div
                            className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl
                                inset-x-3 bottom-3 top-[7.5rem] max-h-[calc(100dvh-8rem)]
                                sm:inset-x-auto sm:right-4 sm:bottom-4 sm:top-24 sm:h-auto sm:w-[min(400px,calc(100vw-2rem))] sm:max-h-[calc(100dvh-7rem)]"
                            role="dialog"
                            aria-label="LAURA Chat"
                        >
                            <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
                                <span className="text-sm font-medium text-gray-900">
                                    LAURA Chat
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setChatOpen(false)}
                                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                                    aria-label="Close chat"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="min-h-0 flex-1">
                                <LauraChatPanel
                                    auditId={audit.id}
                                    currentStage={audit.current_stage}
                                    onDeliverableUpdated={() => reload()}
                                    embedded
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
